import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, shorten } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Trophy, Sparkles, Clock, UserPlus, Loader2 } from "lucide-react";

export default function HallOfExplorers() {
  const { t } = useLang();
  const TABS = [
    { key: "largest_owners", label: t.hall.largest, icon: <Trophy size={14} /> },
    { key: "first_registered_sectors", label: t.hall.first, icon: <Sparkles size={14} /> },
    { key: "recent_discoveries", label: t.hall.recent, icon: <Clock size={14} /> },
    { key: "newest_explorers", label: t.hall.newest, icon: <UserPlus size={14} /> },
  ];
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("largest_owners");

  useEffect(() => { api.get("/hall/explorers").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div className="relative z-10 pt-28 pb-10 max-w-[1500px] mx-auto px-6 md:px-10" data-testid="hall-of-explorers">
      <div className="text-overline mb-4">{t.hall.tag}</div>
      <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tight">{t.hall.title}</h1>
      <p className="mt-6 text-cosmic/70 max-w-2xl font-light leading-relaxed text-lg">
        {t.hall.intro}
      </p>

      <div className="flex flex-wrap gap-2 mt-12 border-b border-cosmic/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`hall-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-5 py-3 font-mono text-xs tracking-widest uppercase border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-portal text-portal" : "border-transparent text-cosmic/50 hover:text-white"
            }`}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-portal" /></div>
      ) : (
        <div className="mt-10">
          {tab === "largest_owners" && <OwnersTable rows={data.largest_owners} />}
          {tab === "first_registered_sectors" && <SectorsTable rows={data.first_registered_sectors} />}
          {tab === "recent_discoveries" && <SectorsTable rows={data.recent_discoveries} />}
          {tab === "newest_explorers" && <ExplorersTable rows={data.newest_explorers} />}
        </div>
      )}
    </div>
  );
}

function Empty() { const { t } = useLang(); return <p className="text-cosmic/50 font-light py-10">{t.hall.empty}</p>; }

function OwnersTable({ rows }) {
  const { t } = useLang();
  if (!rows?.length) return <Empty />;
  return (
    <div className="border border-cosmic/10">
      {rows.map((r, i) => (
        <Link to={`/profile/${r.public_id || r.wallet_address}`} key={r.wallet_address}
          className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-cosmic/10 hover:bg-portal/5 transition-colors items-center">
          <div className="col-span-1 font-display text-xl text-portal">{String(i + 1).padStart(2, "0")}</div>
          <div className="col-span-5">
            <div className="font-grotesk">{r.explorer_name || shorten(r.wallet_address)}</div>
            <div className="font-mono text-xs text-cosmic/50">{r.badge}</div>
          </div>
          <div className="col-span-3 font-mono text-sm text-cosmic/70">{r.sectors_owned} {t.hall.sectors}</div>
          <div className="col-span-3 font-mono text-sm text-cosmic/70 text-right">{r.surface_km2.toLocaleString()} km²</div>
        </Link>
      ))}
    </div>
  );
}

function SectorsTable({ rows }) {
  if (!rows?.length) return <Empty />;
  return (
    <div className="border border-cosmic/10">
      {rows.map((r) => (
        <div key={r.sector_id} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-cosmic/10 items-center text-sm hover:bg-portal/5 transition-colors">
          <div className="col-span-2 font-mono text-portal">{r.sector_id}</div>
          <div className="col-span-4 font-grotesk">{r.name}</div>
          <div className="col-span-3 font-mono text-cosmic/70 text-xs">{r.lat}°, {r.lng}°</div>
          <div className="col-span-3 font-mono text-cosmic/60 text-xs text-right">{shorten(r.owner_wallet)}</div>
        </div>
      ))}
    </div>
  );
}

function ExplorersTable({ rows }) {
  if (!rows?.length) return <Empty />;
  return (
    <div className="border border-cosmic/10">
      {rows.map((r) => (
        <Link to={`/profile/${r.public_id || r.wallet_address}`} key={r.wallet_address}
          className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-cosmic/10 items-center hover:bg-portal/5 transition-colors">
          <div className="col-span-6 font-grotesk">{r.explorer_name || shorten(r.wallet_address)}</div>
          <div className="col-span-3 font-mono text-xs text-cosmic/50">{r.badge}</div>
          <div className="col-span-3 font-mono text-xs text-cosmic/60 text-right">{new Date(r.join_date).toLocaleDateString()}</div>
        </Link>
      ))}
    </div>
  );
}
